import './AddNetworkModal.scss';
import { useMemo, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { LinearCanonicalNetworkEditedRow } from '@blisspointmedia/bpm-types/dist/LinearAssignments';
import { Button } from '@Components/Button';
import { Appearance, Design, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';

interface AddNetworkModalProps {
  show: boolean;
  saving: boolean;
  existingShortCodes: string[];
  existingGroups: string[];
  onHide: () => void;
  onCreate: (network: LinearCanonicalNetworkEditedRow) => void;
}

// Field value unions derived from the canonical-network type, so the dropdowns are typed and the
// selected values flow into buildNetwork without any casts.
type Channel = NonNullable<LinearCanonicalNetworkEditedRow['channel']>;
type Feed = LinearCanonicalNetworkEditedRow['Feed'];
type MediaTier = LinearCanonicalNetworkEditedRow['Media Tier'];

const CHANNEL_OPTIONS = (['Linear', 'Radio'] as const).map(value => ({ label: value, value }));
const FEED_OPTIONS = (['Single', 'Dual', 'Triple', 'Quad'] as const).map(value => ({
  label: value,
  value
}));
const TIER_OPTIONS = (['Lower Tier', 'Mid Tier', 'Upper Tier'] as const).map(value => ({
  label: value,
  value
}));

// A canonical-network row carries many nullable metadata fields; a new network only needs its
// identity + core buy attributes. Everything else starts null and is filled in on the Overview tab.
const buildNetwork = (fields: {
  shortCode: string;
  stationName: string;
  networkGroup: string;
  channel: Channel;
  feed: Feed;
  tier: MediaTier;
}): LinearCanonicalNetworkEditedRow => ({
  'Short Code': fields.shortCode.trim(),
  'Station Name': fields.stationName.trim(),
  'Network Group': fields.networkGroup.trim() || null,
  channel: fields.channel,
  Feed: fields.feed,
  'Media Tier': fields.tier,
  destination_id: null,
  Language: null,
  'SD Only': null,
  'SD Companion': null,
  'S&P Clearance': null,
  'Traffic Endpoint': null,
  'XR Destination': null,
  'HH UEs': null,
  'UEs Likely to Mislead': null,
  Genre: null,
  Color: null,
  'Network Group Color': null
});

export const AddNetworkModal = ({
  show,
  saving,
  existingShortCodes,
  existingGroups,
  onHide,
  onCreate
}: AddNetworkModalProps) => {
  const [shortCode, setShortCode] = useState('');
  const [stationName, setStationName] = useState('');
  const [networkGroup, setNetworkGroup] = useState('');
  const [channel, setChannel] = useState<Channel>('Linear');
  const [feed, setFeed] = useState<Feed>('Single');
  const [tier, setTier] = useState<MediaTier>('Mid Tier');

  const duplicateCode = useMemo(
    () =>
      existingShortCodes.map(code => code.toLowerCase()).includes(shortCode.trim().toLowerCase()),
    [existingShortCodes, shortCode]
  );

  const canSubmit = Boolean(shortCode.trim()) && Boolean(stationName.trim()) && !duplicateCode;

  const handleCreate = () => {
    onCreate(buildNetwork({ shortCode, stationName, networkGroup, channel, feed, tier }));
  };

  return (
    <Modal show={show} onHide={onHide} centered className='add-network-modal'>
      <Modal.Header closeButton>
        <Modal.Title>Add network</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='add-network-form'>
          <Form.Group controlId='add-network-short-code'>
            <Form.Label>Short Code</Form.Label>
            <Form.Control
              value={shortCode}
              isInvalid={duplicateCode}
              placeholder='e.g. AEN'
              onChange={event => setShortCode(event.target.value)}
            />
            {duplicateCode && (
              <Form.Control.Feedback type='invalid'>
                A network with this short code already exists.
              </Form.Control.Feedback>
            )}
          </Form.Group>
          <Form.Group controlId='add-network-station-name'>
            <Form.Label>Station Name</Form.Label>
            <Form.Control
              value={stationName}
              placeholder='e.g. A&E Network'
              onChange={event => setStationName(event.target.value)}
            />
          </Form.Group>
          <div className='add-network-field'>
            <span className='add-network-field-label'>Network Group</span>
            <Dropdown<string>
              size={Size.LG}
              searchable
              creatable
              options={existingGroups.map(group => ({ label: group, value: group }))}
              value={networkGroup || null}
              placeholder='Select or create a group…'
              onChange={value => setNetworkGroup(value ?? '')}
            />
          </div>
          <div className='add-network-selects'>
            <div className='add-network-field'>
              <span className='add-network-field-label'>Channel</span>
              <Dropdown<Channel>
                size={Size.LG}
                options={CHANNEL_OPTIONS}
                value={channel}
                onChange={setChannel}
              />
            </div>
            <div className='add-network-field'>
              <span className='add-network-field-label'>Feed</span>
              <Dropdown<Feed>
                size={Size.LG}
                options={FEED_OPTIONS}
                value={feed}
                onChange={setFeed}
              />
            </div>
            <div className='add-network-field'>
              <span className='add-network-field-label'>Media Tier</span>
              <Dropdown<MediaTier>
                size={Size.LG}
                options={TIER_OPTIONS}
                value={tier}
                onChange={setTier}
              />
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onHide}>
          Cancel
        </Button>
        <Button disabled={!canSubmit} loading={saving} onClick={handleCreate}>
          Add network
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
